import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";
import { cn } from "@/lib/utils";

const brandTextareaVariants = cva(
	"flex w-full border bg-background px-3 py-2 font-mono text-base text-foreground transition-colors placeholder:text-muted-foreground/70 outline-none focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50 resize-none aria-[invalid=true]:border-destructive aria-[invalid=true]:focus-visible:border-destructive",
	{
		variants: {
			variant: {
				default: "border-border focus-visible:border-brand",
				error: "border-destructive focus-visible:border-destructive",
			},
		},
		defaultVariants: {
			variant: "default",
		},
	},
);

export interface BrandTextareaProps
	extends React.TextareaHTMLAttributes<HTMLTextAreaElement>,
		VariantProps<typeof brandTextareaVariants> {
	/** Minimum height in pixels (default: 100) */
	minHeight?: number;
	/** Maximum height in pixels (default: 300) */
	maxHeight?: number;
	ref?: React.Ref<HTMLTextAreaElement>;
}

function BrandTextarea({
	className,
	variant,
	minHeight = 100,
	maxHeight = 300,
	onChange,
	style,
	value,
	defaultValue,
	ref,
	...props
}: BrandTextareaProps) {
	const internalRef = React.useRef<HTMLTextAreaElement>(null);
	const setTextareaRef = React.useCallback(
		(node: HTMLTextAreaElement | null) => {
			internalRef.current = node;
			if (typeof ref === "function") {
				ref(node);
			} else if (ref) {
				ref.current = node;
			}
		},
		[ref],
	);

	const adjustHeight = React.useCallback(() => {
		const textarea = internalRef.current;
		if (!textarea) return;

		textarea.style.height = "auto";
		const newHeight = Math.min(
			Math.max(textarea.scrollHeight, minHeight),
			maxHeight,
		);
		textarea.style.height = `${newHeight}px`;
	}, [minHeight, maxHeight]);

	React.useLayoutEffect(() => {
		adjustHeight();
	});

	const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
		adjustHeight();
		onChange?.(e);
	};

	return (
		<textarea
			className={cn(brandTextareaVariants({ variant }), className)}
			ref={setTextareaRef}
			onChange={handleChange}
			style={{ minHeight, maxHeight, overflow: "auto", ...style }}
			value={value}
			defaultValue={defaultValue}
			{...props}
		/>
	);
}

export { BrandTextarea };
